import * as React from "react";
import {
	Backdrop,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormLabel,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Select,
	Switch,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography	
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import { Menu } from '@mui/material';
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { ADD_NOTIFICATION } from "../../../store/types";
import { createAlertsReport, deleteCustomAlert, getAlertsReports } from "../../../services/alert-report.service";
import { getSharedAlertReports } from "../../../services/alert-report-sharing.service";
import { getCustomAlerts, toggleCustomAlertFacility, toggleSharedCustomAlertFacility } from "../../../services/custom-alert.service";
import { getDefaultAlertFacilityToggles, toggleDefaultAlertFacility } from "../../../services/default-alert-facility-toggle.service";
import { LoadingButton } from "@mui/lab";
import { ALERT_TYPE_TYPE } from "../../../types/common.type";
import { HOSPITAL_CARDS_LABELS } from "../../../types/hospital.type";
import { isOnlyHospitalTabAccess } from "../../../utilis/common";
import { PAGE_TYPE } from "../../../types/pages.type";
import { ADMISSION_CARDS_LABELS } from "../../../types/admission.type";
import { COMMUNITY_CARD_LABELS } from "../../../types/community-transfer.type";
import { DECEASED_CARDS_LABELS } from "../../../types/deceased.type";
import { OVERALL_CARDS_LABELS } from "../../../types/overall.type";
import AlertSettings from "./AlertSetting";
import _, { capitalize, forEach } from "lodash";
import ShareAlertReportDialog from "../ShareAlertReportDialog";
import AlertHistoryDialog from "../AlertHistoryDialog";
import AlertDeleteConfirmationDialog from "./AlertDeleteConfirmationDialog";
import { AUTH_ROLES } from "../../../types/auth.type";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";

// Helper function to remove shared prefix from slug
const removeSharedPrefix = (slug) => {
	if (slug && slug.startsWith('shared_')) {
		return slug.substring(7); // Remove 'shared_' prefix
	}
	return slug;
};

export const RenderCardTypes = ({ formik, handleSelectAll, handleDeleteClick, handleShareClick, handleHistoryClick, FILTER_KEYS, formatLabel, type = "default", formType, getAlertShareStatus, currentUser, isMobile }) => {
	const [menuAnchor, setMenuAnchor] = React.useState(null);
	const [selectedKey, setSelectedKey] = React.useState(null);
	const [searchTerm, setSearchTerm] = React.useState("");

	// Filter FILTER_KEYS based on search term
	const filteredKeys = React.useMemo(() => {
		if (!searchTerm.trim()) {
			return FILTER_KEYS;
		}
		return FILTER_KEYS.filter(key => {
			const label = formatLabel(key);
			return label && label.toLowerCase().includes(searchTerm.toLowerCase());
		});
	}, [FILTER_KEYS, searchTerm, formatLabel]);

	const handleMenuOpen = (event, key) => {
		setMenuAnchor(event.currentTarget);
		setSelectedKey(key);
	};

	const handleMenuClose = () => {
		setMenuAnchor(null);
		setSelectedKey(null);
	};

	const handleMenuAction = (action) => {
		if (action === 'share' && handleShareClick) {
			handleShareClick(formatLabel(selectedKey, "checkLabelReturnObj"));
		} else if (action === 'delete') {
			handleDeleteClick(formatLabel(selectedKey, "checkLabelReturnObj"));
		} else if (action === 'history' && handleHistoryClick) {
			handleHistoryClick(formatLabel(selectedKey, "checkLabelReturnObj"));
		}
		handleMenuClose();
	};

	return (
		<>
			{formType === "userPage" && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-start",
						alignItems: "center",
						width: "100%", // Ensures it spans full width
						p: 0, // Removes any padding
						m: 0, // Removes any margin
					}}
				>
					<Typography
						variant="h6"
						sx={{ mb: 1.5, textAlign: "left" }}
						component="span"
					>
						{capitalize(formik.values.page)} Alerts
					</Typography>
				</Box>
			)}

			<AlertTypeSelect formik={formik} isMobile={isMobile} />

			<Box display="flex" justifyContent="flex-start" alignItems="center" sx={{ mb: 2, ml: 0.2 }}>
				<FormControlLabel
					control={
						<Checkbox
							name="isRising"
							checked={formik.values.isRising}
							onChange={formik.handleChange}
						/>
					}
					sx={{
						...(isMobile && {
							fontSize: "12px",
						})
					}}
					// label="Are Rising"
					label="Trending up"
				/>
				<FormControlLabel
					control={
						<Checkbox
							name="isDropping"
							checked={formik.values.isDropping}
							onChange={formik.handleChange}
						/>
					}
					sx={{
						...(isMobile && {
							fontSize: "12px",
						})
					}}
					label="Trending down"
				/>
			</Box>

			<TextField
				fullWidth
				variant="outlined"
				placeholder="Search alerts..."
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				sx={{ mb: 2 }}
				size={isMobile ? "small" : "medium"}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon sx={{ color: 'text.secondary' }} />
						</InputAdornment>
					),
					endAdornment: searchTerm && (
						<InputAdornment position="end">
							<IconButton
								size="small"
								onClick={() => setSearchTerm("")}
								sx={{ color: 'text.secondary' }}
							>
								<ClearIcon />
							</IconButton>
						</InputAdornment>
					),
				}}
			/>

			<FormControlLabel
				sx={{ mb: 2 }}
				control={
					<Switch
						checked={formik.values.selectAll}
						onChange={handleSelectAll}
					/>
				}
				label="Select all alerts"
			/>

			<Grid container spacing={isMobile ? 1 : 2}>
				{filteredKeys.map((key) => {
					const isCustomAlert = formatLabel(key, "checkLabel");
					const shareStatus = getAlertShareStatus ? getAlertShareStatus(key) : null;

					return (
						<Grid item xs={12} sm={6} md={4} lg={4} key={key}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									position: "relative",
								}}
							>
								{/* Main control row */}
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
									}}
								>
									<FormControlLabel
										control={
											<Switch
												size={type === "settings" ? "medium" : "medium"}
												name={`filters.${key}`}
												checked={formik.values.filters[key]}
												onChange={formik.handleChange}
											/>
										}
										label={
											<Box sx={{ display: 'flex', alignItems: 'center' }}>
												{shareStatus === 'received' && (
													<Tooltip title="Shared with you">
														<CallReceivedIcon sx={{ fontSize: '16px', color: '#4caf50', mr: 1 }} />
													</Tooltip>
												)}
												{shareStatus === 'shared' && (
													<Tooltip title="You have shared this alert with others">
														<CallMadeIcon sx={{ fontSize: '16px', color: '#ff9800', mr: 1 }} />
													</Tooltip>
												)}
												{formatLabel(key)}
											</Box>
										}
									/>
									{isCustomAlert && (
										<Tooltip title="More options">
											<IconButton
												size="small"
												onClick={(e) => handleMenuOpen(e, key)}
												sx={{
													ml: 1,
													opacity: 0.7,
													'&:hover': {
														opacity: 1,
													},
												}}
											>
												<MoreVertIcon sx={{ fontSize: "medium" }} />
											</IconButton>
										</Tooltip>
									)}
								</Box>
							</Box>
						</Grid>
					);
				})}
			</Grid>

			{/* Three-dot menu */}
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleMenuClose}
				PaperProps={{
					sx: {
						boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
						borderRadius: '8px',
						minWidth: 150,
					},
				}}
			>
				{currentUser?.role?.slug !== AUTH_ROLES.USER && (
					<MenuItem
						onClick={() => handleMenuAction('share')}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							py: 1.5,
							'&:hover': {
								backgroundColor: 'rgba(33, 150, 243, 0.08)',
							},
						}}
					>
						<ShareIcon sx={{ fontSize: 18, color: '#1976d2' }} />
						<Typography variant="body2">Share</Typography>
					</MenuItem>
				)}
				<MenuItem
					onClick={() => handleMenuAction('history')}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						py: 1.5,
						'&:hover': {
							backgroundColor: 'rgba(156, 39, 176, 0.08)',
						},
					}}
				>
					<HistoryIcon sx={{ fontSize: 18, color: '#9c27b0' }} />
					<Typography variant="body2">History</Typography>
				</MenuItem>
				<MenuItem
					onClick={() => handleMenuAction('delete')}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						py: 1.5,
						'&:hover': {
							backgroundColor: 'rgba(244, 67, 54, 0.08)',
						},
					}}
				>
					<DeleteIcon sx={{
						fontSize: 18,
						color: '#f44336',
						"@media (max-width: 768px)": {
							fontSize: "14px",
							transform: "scale(0.8)"
						}
					}} />
					<Typography variant="body2" sx={{ color: '#f44336' }}>Delete</Typography>
				</MenuItem>
			</Menu>
		</>

	)
}

export const AlertTypeSelect = ({ formik, isMobile }) => {
	return (
		<FormControl fullWidth sx={{ mb: 2 }}>
			<FormLabel id="alert-type-label">Alert frequency</FormLabel>
			<ToggleButtonGroup
				value={formik.values.alertType}
				onChange={(event, newAlertType) => formik.setFieldValue("alertType", newAlertType)}
				aria-labelledby="alert-type-label"
				color="primary"
				sx={{ display: 'flex' }}
				size="large"
			>
				<ToggleButton sx={{ padding: '8px 16px', ...(isMobile && { fontSize: "14px", padding: "8px 14px" }) }} value={ALERT_TYPE_TYPE.WEEKS}>Weekly</ToggleButton>
				<ToggleButton sx={{ padding: '8px 16px', ...(isMobile && { fontSize: "14px", padding: "8px 14px" }) }} value={ALERT_TYPE_TYPE.BI_WEEKS}>Bi-Weekly</ToggleButton>
				<ToggleButton sx={{ padding: '8px 16px', ...(isMobile && { fontSize: "14px", padding: "8px 14px" }) }} value={ALERT_TYPE_TYPE.MONTHS}>Monthly</ToggleButton>
			</ToggleButtonGroup>
		</FormControl>
	);
};

export default function AlertReport({
	page: pageType,
	activeFacilities,
	type,
	accountFacilities,
	isAccountChecked,
	isChecked,
	selectedAccount,
	isOpenAlertReport,
	dialogType = "default",
	setIsAlertReportOpen,
	selectedSortCut,
	userId,
	dynamicCards
}) {
	const isMobile = useIsMobileDevice();
	const currentUser = useSelector((state) => state.auth);
	const isOnlyHospitalDashboard = React.useMemo(() => {
		return isOnlyHospitalTabAccess();
	}, []);

	const [page, setPage] = React.useState(pageType);
	const [pageLoading, setPageLoading] = React.useState(false);
	const [customAlerts, setCustomAlerts] = React.useState([]);
	// const [alertData, setAlertData] = React.useState(null); // Store alert data with share info

	const getDefaultCardFilter = (page, customAlertsData, dynamicCards = []) => {
		const filters = {
			[PAGE_TYPE.ADMISSION]: {
				total: false,
				admission: false,
				readmission: false,
				doctorData: false,
				daysData: false,
				dxData: false,
				insuranceData: false,
				floorsData: false,
				hospitalData: false,
				averageStayDaysData: false,
			},
			[PAGE_TYPE.COMMUNITY_TRANSFER]: {
				total: false,
				safeDischarge: false,
				SNF: false,
				AMA: false,
				insuranceData: false,
				sixtyDaysData: false,
				returnsData: false,
				doctorData: false,
				floorsData: false,
			},
			[PAGE_TYPE.DECEASED]: {
				total: false,
				insuranceData: false,
				doctorData: false,
				floorsData: false,
				ninetyDaysData: false,
			},
			[PAGE_TYPE.OVERALL]: {
				totalIncoming: false,
				totalOutgoing: false,
				insuranceData: false,
				doctorData: false,
				floorsData: false,
				ninetyDaysData: false,
			},
			[PAGE_TYPE.HOSPITAL]: {
				total: false,
				unplannedHospitalTransfer: false,
				plannedHospitalTransfer: false,
				hospitalizations: false,
				DCERData: false,
				insuranceData: false,
				returnsData: false,
				ninetyDaysData: false,
				doctorData: false,
				nurseData: false,
				floorsData: false,
				daysData: false,
				dxData: false,
				shiftData: false,
				hospitalData: false,
			},
		};

		if (customAlertsData.length > 0) {
			forEach(customAlertsData, (item) => {
				filters[page][item.slug] = false;
			});
		}
		if (dynamicCards?.length > 0) {
			forEach(dynamicCards, (item) => {
				filters[page][item.accessor] = false;
			});
		}

		return filters[page] || {}; // Return an empty object for unknown pages
	};

	const DEFAULT_CARD_FILTER = React.useMemo(() => getDefaultCardFilter(page, customAlerts, dynamicCards), [page, customAlerts, dynamicCards]);

	const [isOpen, setIsOpen] = React.useState(false);
	const [loading, setLoading] = React.useState(true);
	const [selectedAlertId, setSelectedAlertId] = React.useState(null);

	const FILTER_KEYS_DATA = Object.keys(DEFAULT_CARD_FILTER).filter(
		(key) => !(isOnlyHospitalDashboard && key === 'returnsData')
	);

	const FILTER_KEYS = React.useMemo(() => {
		return FILTER_KEYS_DATA
	}, [FILTER_KEYS_DATA]);

	const [initialValues, setInitialValues] = React.useState({
		filters: FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: false }), {}),
		alertType: [ALERT_TYPE_TYPE.WEEKS],
		isRising: false,
		isDropping: false,
		isTransferNeedWork: false,
		selectAll: false,
		page: page
	});
	const [facilitiesList, setFacilitiesList] = React.useState([]);
	const [accountList, setAccountList] = React.useState([]);
	const dispatch = useDispatch();

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			...initialValues
		},
		onSubmit: (values, { setSubmitting }) => {
			handleSave(values);
			setSubmitting(false);
		},
	});

	const handleClose = React.useCallback(() => {
		setIsOpen(false);
		setIsAlertReportOpen && setIsAlertReportOpen(false);
		formik?.setFieldValue("facilityId", []);
		formik?.resetForm();
		setInitialValues({
			filters: FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: false }), {}),
			alertType: [ALERT_TYPE_TYPE.WEEKS],
			isRising: false,
			isDropping: false,
			selectAll: false,
			page
		});
	}, [setIsAlertReportOpen, formik, FILTER_KEYS, page]);

	React.useEffect(() => {
		if (isOpenAlertReport) {
			setIsOpen(true);
		}
	}, [isOpenAlertReport]);

	React.useMemo(() => {
		if (accountFacilities && type === "userPage") {
			setAccountList([]);
			setFacilitiesList([]);
			const selectedAccounts = accountFacilities.filter((itemAccount) => {
				const checkResult = isAccountChecked("access", itemAccount);
				const checkResult2 = isAccountChecked("read", itemAccount);
				const checkResult3 = isAccountChecked("write", itemAccount);
				return (checkResult || checkResult2 || checkResult3) && itemAccount._id?._id;
			}).map((itemAccount) => itemAccount._id);
			setAccountList(selectedAccounts);
		}
	}, [accountFacilities, type, isAccountChecked]);

	const getAlertsReportsDB = React.useCallback(async (facilityId = null, pageType = null) => {
		setLoading(true);
		try {
			let userIdData = type === "userPage" ? userId : null;

			// First, fetch basic alert reports and shared data
			const [ownedResponse, sharedResponse] = await Promise.all([
				getAlertsReports({
					page: pageType ? pageType : page,
					facilityId,
					userId: userIdData,
					...type === "userPage" && { accountId: formik.values?.accountId }
				}),
				getSharedAlertReports(pageType ? pageType : page, true) // includeOwned = true to get share status
			]);

			// Process owned alert data
			const ownedAlert = ownedResponse;
			const sharedAlerts = sharedResponse?.data?.shared || [];
			const ownedAlertsWithShareInfo = sharedResponse?.data?.owned || [];

			// Then fetch custom alerts and facility toggles based on the alert report
			let fetchedCustomAlerts = [];
			let facilityToggles = {};

			if (ownedAlert?._id) {
				const [customAlertsResponse, facilityTogglesResponse] = await Promise.all([
					getCustomAlerts({
						page: pageType ? pageType : page,
						alertReportId: ownedAlert._id
					}),
					getDefaultAlertFacilityToggles({
						page: pageType ? pageType : page,
						alertReportId: ownedAlert._id
					})
				]);

				fetchedCustomAlerts = customAlertsResponse?.data || [];
				facilityToggles = facilityTogglesResponse?.data || {};
			}

			// Find the owned alert with share information for this facility
			// let ownedAlertWithShareInfo = null;
			// if (facilityId && ownedAlertsWithShareInfo.length > 0) {
			// 	ownedAlertWithShareInfo = ownedAlertsWithShareInfo.find(alert => 
			// 		alert.facilityId?.toString() === facilityId?.toString()
			// 	);
			// }

			// Get global share information across all facilities for owned alerts
			const globalShareInfo = ownedAlertsWithShareInfo.reduce((acc, alert) => {
				if (alert.shareInfo) {
					// Merge shared custom alert slugs from all facilities
					if (alert.shareInfo.sharedCustomAlertSlugs) {
						acc.sharedCustomAlertSlugs = [
							...(acc.sharedCustomAlertSlugs || []),
							...alert.shareInfo.sharedCustomAlertSlugs
						];
					}
					// If any facility has shared the full report, mark it as shared
					if (alert.shareInfo.hasSharedFullReport) {
						acc.hasSharedFullReport = true;
					}
					// If any facility has shared to others, mark it as shared
					if (alert.shareInfo.hasSharedToOthers) {
						acc.hasSharedToOthers = true;
					}
				}
				return acc;
			}, {});

			// Remove duplicates from sharedCustomAlertSlugs
			if (globalShareInfo.sharedCustomAlertSlugs) {
				globalShareInfo.sharedCustomAlertSlugs = [...new Set(globalShareInfo.sharedCustomAlertSlugs)];
			}

			// Find current facility's alert from shared alerts
			let currentAlert = ownedAlert;
			let isSharedAlert = false;

			// If no owned alert but we have shared alerts for this facility, use the first shared one

			if (!currentAlert && sharedAlerts.length > 0 && facilityId) {
				const matchingSharedAlert = sharedAlerts.find(alert =>
					alert.facilityId?.toString() === facilityId?.toString()
				);
				if (matchingSharedAlert) {
					currentAlert = matchingSharedAlert;
					isSharedAlert = true;
				}
			}

			// Build custom alerts data from the new CustomAlert API response
			const customAlertsData = fetchedCustomAlerts.map((item) => {
				let shareInfo = null;

				if (isSharedAlert) {
					// If this is a shared alert, use the share info from current alert
					shareInfo = currentAlert?.shareInfo;
				} else if (globalShareInfo.sharedCustomAlertSlugs || globalShareInfo.hasSharedFullReport) {
					// For owned alerts, check if this specific custom alert has been shared across any facility
					const hasSharedThisCustomAlert = globalShareInfo.sharedCustomAlertSlugs?.includes(item.slug);

					shareInfo = {
						isOwned: true,
						hasSharedToOthers: !!hasSharedThisCustomAlert, // Only true if THIS alert was shared
						hasSharedFullReport: globalShareInfo.hasSharedFullReport
					};
				}

				return {
					slug: item.slug,
					name: item.name,
					isCustom: true,
					_id: item._id,
					shareInfo
				};
			});

			// Add custom alerts from shared alerts

			sharedAlerts.forEach(sharedAlert => {
				// Handle shared alerts with customAlert property (singular)
				if (sharedAlert.customAlert) {
					const customAlert = sharedAlert.customAlert;
					// Add shared prefix to slug for shared alerts
					const sharedSlug = `shared_${customAlert.slug}`;

					// Check if this custom alert is not already in the list

					const existingAlert = customAlertsData.find(alert => alert.slug === sharedSlug);

					if (!existingAlert) {
						customAlertsData.push({
							slug: sharedSlug, // Use prefixed slug for display
							originalSlug: customAlert.slug, // Keep original slug for reference
							name: customAlert.name,
							isCustom: true,
							newName: sharedAlert?.shareInfo?.newName,
							_id: customAlert._id,
							shareInfo: {
								...sharedAlert.shareInfo,
								// Mark as received if it's a custom alert share
								isShared: sharedAlert.shareInfo?.isCustomAlertShare ? true : sharedAlert.shareInfo?.isShared
							}
						});
					}
				}
				// Handle shared alerts with customAlerts property (plural) - for backward compatibility
				else if (sharedAlert.customAlerts && sharedAlert.customAlerts.length > 0) {
					sharedAlert.customAlerts.forEach(customAlert => {
						// Add shared prefix to slug for shared alerts
						const sharedSlug = `shared_${customAlert.slug}`;

						// Check if this custom alert is not already in the list
						const existingAlert = customAlertsData.find(alert => alert.slug === sharedSlug);

						if (!existingAlert) {
							customAlertsData.push({
								slug: sharedSlug, // Use prefixed slug for display
								originalSlug: customAlert.slug, // Keep original slug for reference
								name: customAlert.name,
								newName: sharedAlert.shareInfo?.newName,
								isCustom: true,
								_id: customAlert._id,
								shareInfo: {
									...sharedAlert.shareInfo,
									// Mark as received if it's a custom alert share
									isShared: sharedAlert.shareInfo?.isCustomAlertShare ? true : sharedAlert.shareInfo?.isShared
								}
							});
						}
					});
				}
			});

			const DEFAULT_CARD_FILTER_DATA = getDefaultCardFilter(pageType ? pageType : page, customAlertsData, dynamicCards);


			const FILTER_KEYS_DATA = Object.keys(DEFAULT_CARD_FILTER_DATA).filter(
				(filterKey) => !(isOnlyHospitalDashboard && filterKey === 'returnsData')
			);

			if (currentAlert) {
				setSelectedAlertId(currentAlert?._id);
				setCustomAlerts(customAlertsData);

				// Build facility-specific filter values
				const updatedInitialValues = FILTER_KEYS_DATA.reduce((acc, key) => {
					// Check if this is a custom alert
					const isCustomAlert = customAlertsData.find(alert => alert.slug === key);

					if (isCustomAlert) {
						// Check if this is a shared custom alert
						if (isCustomAlert.originalSlug) {
							// For shared custom alerts, check facility toggles from shared alert record
							const sharedAlert = sharedAlerts.find(alert =>
								alert.customAlert && alert.customAlert.slug === isCustomAlert.originalSlug
							);

							if (sharedAlert && sharedAlert.facilityToggles && facilityId) {
								// Check if this facility is enabled in the shared alert's facility toggles
								// Convert facilityId to string for comparison
								const facilityIdStr = String(facilityId);
								acc[key] = sharedAlert.facilityToggles[facilityIdStr] === true;
							} else {
								acc[key] = false;
							}
						} else {
							// For owned custom alerts, check facility toggles from fetchedCustomAlerts
							const customAlert = fetchedCustomAlerts.find(alert => alert.slug === key);
							if (customAlert && facilityId) {
								const facilityToggle = customAlert.facilityToggles?.find(toggle =>
									toggle.facilityId === facilityId
								);
								acc[key] = facilityToggle ? facilityToggle.enabled : false;
							} else {
								acc[key] = false;
							}
						}
					} else {
						// For default alerts, check facility toggles
						if (facilityToggles[key] && facilityId) {
							const facilityToggle = facilityToggles[key].find(toggle =>
								toggle.facilityId === facilityId
							);
							acc[key] = facilityToggle ? facilityToggle.enabled : false;
						} else {
							// Fallback to generic alerts if no facility-specific toggle found
							acc[key] = currentAlert?.alerts?.[key] !== undefined ? currentAlert.alerts[key] : false;
						}
					}
					return acc;
				}, {});

				setLoading(false);
				formik?.setFieldValue("filters", updatedInitialValues);
				formik?.setFieldValue("alertType", currentAlert?.type);
				formik?.setFieldValue("isRising", currentAlert?.isRising);
				formik?.setFieldValue("isDropping", currentAlert?.isDropping);
				//formik?.setFieldValue("isTransferNeedWork", currentAlert?.isTransferNeedWork);
				formik?.setFieldValue("type", currentAlert?.type);
				formik?.setFieldValue("selectAll", Object.values(updatedInitialValues).every(value => value === true));
				if (type === "userPage") {
					formik?.setFieldValue("facilityId", [currentAlert?.facilityId]);
					formik?.setFieldValue("accountId", currentAlert?.accountId);
				}

				// Store original form values after setting them
				setOriginalFormValues({
					filters: updatedInitialValues,
					alertType: currentAlert?.type,
					isRising: currentAlert?.isRising,
					isDropping: currentAlert?.isDropping,
					type: currentAlert?.type,
					selectAll: Object.values(updatedInitialValues).every(value => value === true),
					...(type === "userPage" && {
						facilityId: [currentAlert?.facilityId],
						accountId: currentAlert?.accountId
					})
				});
			} else {
				// If no alert data found, set customAlerts from all shared alerts
				setCustomAlerts(customAlertsData);
				formik?.setFieldValue("filters", FILTER_KEYS_DATA.reduce((acc, key) => ({ ...acc, [key]: false }), {}));
				formik?.setFieldValue("alertType", [ALERT_TYPE_TYPE.WEEKS]);
				formik?.setFieldValue("isRising", false);
				formik?.setFieldValue("isDropping", false);
				// formik?.setFieldValue("isTransferNeedWork", false);
				formik?.setFieldValue("selectAll", false);
				setLoading(false);
				setOriginalFormValues({}); // Clear original form values if no alert data
			}
		} catch (error) {
			setLoading(false);
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: "Failed to load alert settings",
					id: "loadSettingsError",
				},
			});
		} finally {
			setLoading(false);
		}
	}, [type, userId, page, formik, isOnlyHospitalDashboard, dispatch, dynamicCards]);

	// Add state to track original form values for comparison
	const [originalFormValues, setOriginalFormValues] = React.useState({});

	const handleUpdateDataByPage = React.useCallback(async (page) => {
		if (formik.values?.facilityId && formik.values?.facilityId.length === 1) {
			await getAlertsReportsDB(formik.values?.facilityId[0], page);
			setPageLoading(false);
		} else {
			setPageLoading(false);
		}
	}, [formik?.values]); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		setPageLoading(true);
		setPage(formik.values?.page);
		handleUpdateDataByPage(formik.values?.page);
	}, [formik.values?.page]); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		if (isOpen) {
			if (type !== "userPage") {
				getAlertsReportsDB(activeFacilities?.[0]);
			} else {
				setLoading(false);
			}
		}
	}, [isOpen, activeFacilities]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleSave = React.useCallback(
		async (valuesData) => {
			try {
				if (type === "userPage" && !valuesData?.accountId) {
					dispatch({
						type: ADD_NOTIFICATION,
						payload: {
							type: "error",
							label: "Please select account",
							id: "alertSettingsSaveError",
						},
					});
					return;
				}

				if (type === "userPage" && valuesData?.facilityId?.length === 0) {
					dispatch({
						type: ADD_NOTIFICATION,
						payload: {
							type: "error",
							label: "Please select at least one facility",
							id: "alertSettingsSaveError",
						},
					});
					return;
				}

				let userIdData = type === "userPage" ? userId : null;
				const allFacilityIds = type === "userPage" ? valuesData?.facilityId : activeFacilities;

				// First, save the basic alert report settings (for backward compatibility)
				const updateData = {
					alerts: valuesData?.filters,
					page,
					facilityIds: allFacilityIds,
					type: valuesData?.alertType,
					isRising: valuesData?.isRising,
					isDropping: valuesData?.isDropping,
					...userId && { userId: userIdData },
					...type === "userPage" && { accountId: valuesData?.accountId }
				};
				const alertReportRes = await createAlertsReport(updateData);

				if (alertReportRes && allFacilityIds && allFacilityIds.length > 0) {
					// Only save facility-specific toggles for alert keys that were actually changed
					const togglePromises = [];

					// Compare current form values with original values to find changes
					const currentFilters = valuesData?.filters || {};
					const originalFilters = originalFormValues?.filters || {};

					// Find only the alert keys that were changed
					const changedAlertKeys = Object.keys(currentFilters).filter(alertKey => {
						return currentFilters[alertKey] !== originalFilters[alertKey];
					});
					// For each facility, save toggles only for changed alerts
					allFacilityIds.forEach(facilityId => {
						changedAlertKeys.forEach(alertKey => {
							const enabled = currentFilters[alertKey];

							// Check if this is a custom alert
							const isCustomAlert = customAlerts.find(alert => alert.slug === alertKey);

							if (isCustomAlert) {
								// For shared alerts, we need to use the original custom alert ID
								let customAlertId = isCustomAlert._id;

								// If this is a shared alert (has originalSlug), use the original custom alert ID
								if (isCustomAlert.originalSlug) {
									customAlertId = isCustomAlert._id;
								}

								// Save custom alert facility toggle for this facility
								if (isCustomAlert.originalSlug) {
									// This is a shared custom alert
									togglePromises.push(
										toggleSharedCustomAlertFacility(customAlertId, facilityId, enabled)
									);
								} else {
									// This is an owned custom alert
									togglePromises.push(
										toggleCustomAlertFacility(customAlertId, facilityId, enabled)
									);
								}
							} else {
								// Save default alert facility toggle for this facility
								// Handle both single report and array of reports from backend
								let reportId = null;

								if (Array.isArray(alertReportRes)) {
									// If it's an array, find the report for this specific facility
									const reportForFacility = alertReportRes.find(report =>
										report.facilityId?.toString() === facilityId?.toString()
									);
									reportId = reportForFacility?._id;
								} else if (alertReportRes && alertReportRes._id) {
									// If it's a single report, use its ID
									reportId = alertReportRes._id;
								}

								if (reportId) {
									// Define customAlertSlug using alertKey, removing shared prefix if present
									const customAlertSlug = typeof alertKey === 'string' && alertKey.startsWith('shared_') ? alertKey.substring(7) : alertKey;
									togglePromises.push(
										toggleDefaultAlertFacility(reportId, alertKey, facilityId, enabled, customAlertSlug)
									);
								}
							}
						});
					});

					// Wait for all toggles to be saved
					await Promise.all(togglePromises);
				}

				dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "success",
						label: "Alert settings updated successfully",
						id: "alertSettingsUpdated",
					},
				});
				setIsAlertReportOpen && setIsAlertReportOpen(false);
				handleClose();
			} catch (error) {
				console.error('Error saving alert settings:', error);
				dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "error",
						label: "Failed to save alert settings",
						id: "alertSettingsSaveError",
					},
				});
				setIsAlertReportOpen && setIsAlertReportOpen(false);
				handleClose();
			}
		},
		[type, userId, activeFacilities, page, dispatch, setIsAlertReportOpen, handleClose, originalFormValues?.filters, customAlerts] // Add originalFormValues to dependencies
	);

	const handleSelectAll = React.useCallback((event) => {
		const isChecked = event.target.checked;
		formik.setFieldValue('selectAll', isChecked);
		FILTER_KEYS.forEach((key) => {
			formik.setFieldValue(`filters.${key}`, isChecked);
		});
	}, [formik, FILTER_KEYS]);

	const formatLabel = React.useCallback((key, needCustomLabelCheck = "default") => {
		let label;
		switch (page) {
			case PAGE_TYPE.ADMISSION:
				label = ADMISSION_CARDS_LABELS[key];
				break;
			case PAGE_TYPE.COMMUNITY_TRANSFER:
				label = COMMUNITY_CARD_LABELS[key];
				break;
			case PAGE_TYPE.DECEASED:
				label = DECEASED_CARDS_LABELS[key];
				break;
			case PAGE_TYPE.HOSPITAL:
				label = HOSPITAL_CARDS_LABELS[key];
				break;
			case PAGE_TYPE.OVERALL:
				label = OVERALL_CARDS_LABELS[key];
				break;
			default:
				label = null;
		}
		if (needCustomLabelCheck === "checkLabel" && label) {
			return false;
		} else if (needCustomLabelCheck === "checkLabelReturnObj" && label) {
			return null;
		}
		if (!label && dynamicCards?.length > 0) {
			let customCardLabelItem = _.find(dynamicCards, { accessor: key });
			label = customCardLabelItem?.tableLabel || customCardLabelItem?.label;

			if (label) {
				return needCustomLabelCheck === "default" ? label : null;
			}
		}
		if (!label && customAlerts.length > 0) {

			let customLabel = _.find(customAlerts, { slug: key })?.newName || _.find(customAlerts, { slug: key })?.name;
			label = customLabel ? customLabel : null;
			if (needCustomLabelCheck === "checkLabel") {
				return customLabel ? true : false;
			} else if (needCustomLabelCheck === "checkLabelReturnObj" && customLabel) {
				return _.find(customAlerts, { slug: key });
			}
		}
		return label;
	}, [page, dynamicCards, customAlerts]);

	const handleOnChangeFacility = React.useCallback(async (e) => {
		if (e && e.length === 1) {
			await getAlertsReportsDB(e[0]);
		} else {
			formik?.setFieldValue("filters", FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: false }), {}));
			formik?.setFieldValue("alertType", [ALERT_TYPE_TYPE.WEEKS]);
			formik?.setFieldValue("isRising", false);
			formik?.setFieldValue("isDropping", false);
			// formik?.setFieldValue("isTransferNeedWork", false);
		}
	}, [getAlertsReportsDB, formik, FILTER_KEYS]);

	const handleOnChangeAccount = React.useCallback((e) => {
		formik?.setFieldValue("accountId", e);
		formik?.setFieldValue("facilityId", []);

		const selectedFacilities = accountFacilities.flatMap((item) => {
			if (e === item._id?._id) {
				return item.facilities?.filter((facility) => isChecked(facility._id, "read")) || [];
			}
			return [];
		});

		setFacilitiesList(selectedFacilities);

	}, [accountFacilities, formik, isChecked]);

	// Extracted form controls for cleaner JSX

	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
	const [selectedKey, setSelectedKey] = React.useState(null);
	const [isDeleting, setIsDeleting] = React.useState(false);
	const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);
	const [selectedShareKey, setSelectedShareKey] = React.useState(null);
	const [isHistoryDialogOpen, setIsHistoryDialogOpen] = React.useState(false);
	const [selectedHistoryKey, setSelectedHistoryKey] = React.useState(null);

	React.useEffect(() => {
		// selectedHistoryKey tracking
	}, [selectedHistoryKey]);

	const handleDeleteClick = (key) => {
		setSelectedKey(key);
		setIsDeleteDialogOpen(true);
	};

	const handleShareClick = (key) => {
		// Check if this is a custom alert or full alert report
		const isCustomAlert = key && key.slug; // If key has a slug, it's a custom alert

		if (isCustomAlert) {
			// Remove shared prefix from slug for API calls
			const cleanSlug = removeSharedPrefix(key.slug);

			// For custom alerts, create a specific sharing object
			const customAlertShareData = {
				_id: selectedAlertId, // The parent alert report ID
				name: key.name,
				page: page,
				type: formik.values.alertType,
				isRising: formik.values.isRising,
				isDropping: formik.values.isDropping,
				alerts: { [cleanSlug]: true }, // Only this custom alert
				customAlert: [key], // Only the shared custom alert
				isActive: true,
				shareType: "CUSTOM_ALERT_ONLY",
				customAlertSlug: cleanSlug,
				customAlertData: key
			};
			setSelectedShareKey(customAlertShareData);
		} else {
			// For full alert report sharing
			const alertReportData = {
				_id: selectedAlertId,
				name: `${capitalize(page)} Alert Report`,
				page: page,
				type: formik.values.alertType,
				isRising: formik.values.isRising,
				isDropping: formik.values.isDropping,
				alerts: formik.values.filters,
				customAlert: customAlerts, // All custom alerts
				isActive: true,
				shareType: "FULL_ALERT_REPORT"
			};
			setSelectedShareKey(alertReportData);
		}

		setIsShareDialogOpen(true);
	};

	const handleHistoryClick = (key) => {
		// If key is a string, treat it as the slug; if it's an object, get the slug property
		const customAlertSlug = typeof key === 'string' ? key : key?.slug;

		// Find the custom alert object if possible
		const customAlert = typeof key === 'object' ? key : customAlerts.find(a => a.slug === key);
		const alertName = customAlert?.name || formatLabel(key) || `${capitalize(page)} Alert`;

		// Extract the correct alert report ID for shared alerts
		let alertReportId = selectedAlertId;

		if (customAlert?._id) {
			// Check if this is a shared custom alert (ID format: originalAlertReportId_customAlertSlug)
			if (customAlert.shareInfo?.creatorAlertId) {
				// Extract original alert report ID from combination ID
				alertReportId = customAlert.shareInfo.creatorAlertId;
			} else {
				// Use the alert report ID for owned custom alerts
				alertReportId = selectedAlertId;
			}
		}

		// Remove shared prefix from slug for API calls
		const cleanCustomAlertSlug = removeSharedPrefix(customAlertSlug);

		const historyData = {
			_id: alertReportId,
			name: alertName,
			slug: key,
			isCustomAlert: !!customAlert,
			customAlertSlug: cleanCustomAlertSlug // Use clean slug for API calls
		};
		setSelectedHistoryKey(historyData);
		setIsHistoryDialogOpen(true);
	};

	const handleCloseDelete = () => {
		setIsDeleteDialogOpen(false);
		setSelectedKey(null);
	};

	const handleCloseShare = () => {
		setIsShareDialogOpen(false);
		setSelectedShareKey(null);
	};

	const handleCloseHistory = () => {
		setIsHistoryDialogOpen(false);
		setSelectedHistoryKey(null);
	};

	const handleConfirmDelete = async (deleteForAll = false) => {
		setIsDeleting(true);
		try {

			// Helper function to remove all data after underscore and the underscore itself
			const processSelectedAlertId = (alertId) => {
				if (!alertId) return alertId;
				// If selectedAlertId contains underscores, remove all data after first underscore and the underscore itself
				if (alertId.includes('_')) {
					return alertId.split('_')[0];
				}
				return alertId;
			};

			const resData = await deleteCustomAlert({
				...selectedKey,
				selectedAlertId: selectedAlertId ? processSelectedAlertId(selectedAlertId) : null,
				deleteForAll, // Pass the deleteForAll flag to backend
				// For shared custom alerts, pass the creatorAlertId
				...(selectedKey?.shareInfo?.creatorAlertId && {
					creatorAlertId: selectedKey.shareInfo.creatorAlertId
				})
			});

			if (resData && resData.success) {
				if (type === "userPage") {
					handleClose()
				} else {
					getAlertsReportsDB(activeFacilities?.[0]);
				}

				dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "success",
						label: deleteForAll
							? `Custom alert deleted for all users successfully`
							: `Custom alert deleted successfully`,
						id: "customAlertDeleted",
					},
				});
			} else {
				dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						label: resData?.message || "Failed to delete custom alert",
						type: "error",
						id: "customAlertDeletedErr",
					},
				});
			}
		} catch (error) {
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					label: "Failed to delete custom alert",
					type: "error",
					id: "customAlertDeletedErr",
				},
			});
		} finally {
			setIsDeleting(false);
			handleCloseDelete();
		}
	};

	// Helper function to determine alert share status
	const getAlertShareStatus = (key) => {
		// Only show share status for custom alerts, not default alerts
		const isCustomAlert = formatLabel(key, "checkLabel");
		if (!isCustomAlert) {
			return null; // No status for default alerts
		}

		const customAlert = customAlerts.find(alert => alert.slug === key);
		if (!customAlert?.shareInfo) {
			return null;
		}

		// Check if this is a shared custom alert (received from someone else)
		if (customAlert.shareInfo.isCustomAlertShare || customAlert.shareInfo.isShared) {
			return 'received';
		}

		// Check if this custom alert has been shared to others by current user
		if (customAlert.shareInfo.hasSharedToOthers) {
			return 'shared';
		}

		// Don't show "owned" status anymore - only show shared/received
		return null;
	};


	const renderContent = () => (
		<form onSubmit={formik.handleSubmit}>
			<FormGroup>
				{type === "userPage" && (
					<>
						<FormControl fullWidth sx={{ mb: 2 }}>
							<FormLabel id="account-type-label">Account</FormLabel>
							<Select
								labelId="account-type-label"
								name="accountId"
								value={formik.values.accountId || ""}
								onChange={(e) => {
									const { value } = e.target;
									formik.setFieldValue("accountId", value);
									handleOnChangeAccount(value);
								}}
								renderValue={(selected) =>
									accountList.find((item) => item._id === selected)?.name || "Select Account"
								}
							>
								{accountList?.map((item) => (
									<MenuItem key={item._id} value={item._id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl fullWidth sx={{ mb: 2 }}>
							<FormLabel id="alert-type-label">Facility</FormLabel>
							<Select
								labelId="alert-type-label"
								name="facilityId"
								value={formik.values.facilityId || []}
								onChange={(e) => {
									const { value } = e.target;
									const allIds = facilitiesList.map((item) => item._id);

									if (value.includes("all")) {
										// If "Select All" is clicked
										formik.setFieldValue(
											"facilityId",
											formik.values?.facilityId?.length === facilitiesList?.length ? [] : allIds
										);
										handleOnChangeFacility(formik.values?.facilityId?.length === facilitiesList?.length ? [] : allIds)
									} else {
										// If individual items are clicked
										formik.setFieldValue("facilityId", value);
										handleOnChangeFacility(value)
									}
								}}
								multiple
								renderValue={(selected) =>
									facilitiesList
										.filter((item) => selected.includes(item._id))
										.map((item) => item.name)
										.join(", ")
								}
							>
								{/* Select All Option */}
								<MenuItem value="all">
									<Checkbox
										checked={formik.values?.facilityId?.length === facilitiesList?.length}
										indeterminate={
											formik.values?.facilityId?.length > 0 &&
											formik.values.facilityId?.length < facilitiesList?.length
										}
									/>
									Select All
								</MenuItem>

								{/* Individual Facility Options */}
								{facilitiesList?.map((item) => (
									<MenuItem key={item._id} value={item._id}>
										<Checkbox checked={formik.values?.facilityId?.includes(item._id)} />
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</>
				)}

				{type !== "userPage" &&
					<RenderCardTypes
						formik={formik}
						handleSelectAll={handleSelectAll}
						handleShareClick={handleShareClick}
						handleHistoryClick={handleHistoryClick}
						FILTER_KEYS={FILTER_KEYS}
						formatLabel={formatLabel}
						page={formik.values.page}
						formType={type}
						handleDeleteClick={handleDeleteClick}
						getAlertShareStatus={getAlertShareStatus}
						currentUser={currentUser}
						isMobile={isMobile}
					/>
				}
				{
					type === "userPage" && (
						<AlertSettings
							formik={formik}
							handleSelectAll={handleSelectAll}
							handleShareClick={handleShareClick}
							handleHistoryClick={handleHistoryClick}
							FILTER_KEYS={FILTER_KEYS}
							formatLabel={formatLabel}
							page={formik.values.page}
							pageLoading={pageLoading}
							handleDeleteClick={handleDeleteClick}
							getAlertShareStatus={getAlertShareStatus}
							formType={type}
							currentUser={currentUser}
							isMobile={isMobile}
						/>
					)
				}
			</FormGroup>
		</form>
	);


	return (
		<>
			{(selectedSortCut?.isAlertsReport || type === "userPage") && !isMobile && (
				<Box>
					<Tooltip title="Manage Alerts" arrow>
						<IconButton
							onClick={() => setIsOpen(true)}
							sx={{
								width: 30,
								height: 30,
								ml: 1,
								borderRadius: "4px",
								'&:hover': {
									backgroundColor: '#4879f5',
									color: "white",
								},
							}}
						>
							<NotificationsActiveOutlinedIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			)}
			{isOpen && (
				<Dialog
					fullScreen={isMobile}
					fullWidth
					maxWidth={isMobile ? false : "md"}
					open={isOpen}
					onClose={handleClose}
				>
					<DialogTitle>{`${capitalize(formik.values.page)} Alerts`}</DialogTitle>

					<DialogContent>
						{loading && (
							<Backdrop open={true} style={{
								position: 'absolute', top: 0, left: 0, zIndex: 5,
								backgroundColor: 'rgba(255, 255, 255, 0.2)', // Slightly translucent
								backdropFilter: 'blur(0.5px)', // Applies a blur effect
							}}>
								<CircularProgress color="primary" />
							</Backdrop>
						)}
						{renderContent()}
					</DialogContent>
					<DialogActions>
						<Box flex="1" display="flex" justifyContent="flex-end">
							<Button onClick={handleClose} variant="outlined">Close</Button>
							<LoadingButton
								type="submit"
								variant="contained"
								color="primary"
								onClick={formik.submitForm}
								sx={{ marginLeft: 2 }}
								loading={formik.isSubmitting}
							>
								Save
							</LoadingButton>
						</Box>
					</DialogActions>
				</Dialog>
			)}
			{/* Confirm Delete Dialog */}
			<AlertDeleteConfirmationDialog
				open={isDeleteDialogOpen}
				onClose={handleCloseDelete}
				onConfirm={handleConfirmDelete}
				alertData={selectedKey}
				isLoading={isDeleting}
			/>

			{/* Share Alert Dialog */}
			{isShareDialogOpen && selectedShareKey && (
				<ShareAlertReportDialog
					alertReport={selectedShareKey}
					onClose={handleCloseShare}
					onShareSuccess={() => {
						handleCloseShare();
						// Optionally refresh the alert data
						if (type === "userPage") {
							handleClose();
						} else {
							getAlertsReportsDB(activeFacilities?.[0]);
						}
					}}
				/>
			)}

			{/* History Dialog */}
			{isHistoryDialogOpen && selectedHistoryKey && (
				<AlertHistoryDialog
					open={isHistoryDialogOpen}
					alertReportId={selectedHistoryKey?._id || selectedAlertId}
					alertName={selectedHistoryKey?.name || `Alert: ${formatLabel(selectedHistoryKey?.slug || '')}`}
					customAlertSlug={selectedHistoryKey?.customAlertSlug}
					onClose={handleCloseHistory}
				/>
			)}
		</>
	);
}